<template>
  <div class="task-panel" :style="panelStyle">
    <div class="task-panel-inner">
      <div
        v-for="(task, index) in orderedTasks"
        :key="task.id"
        class="task-card"
        tabindex="0"
        @click="handleSelectTask(task)"
        @keydown.enter.prevent="handleSelectTask(task)"
        @keydown.space.prevent="handleSelectTask(task)"
      >
        <div class="task-card-header">
          <div class="task-icon-wrap">
            <img
              :src="taskIcon"
              class="task-icon"
              :alt="t('taskPanel.taskIconAlt')"
            />
          </div>
          <span class="task-name">{{ task.collectionTaskName }}</span>
          <span class="task-date">{{ task.taskName }}</span>
        </div>

        <div class="task-card-body">
          <div class="task-progress">
            <span class="task-status" :class="task.statusTone">{{
              task.statusLabel
            }}</span>
          </div>
          <div class="task-actions">
            <!-- <button
              class="icon-btn"
              :class="{ disabled: index === 0 }"
              @click.stop="moveTask(index, -1)"
              :aria-label="t('taskPanel.moveUp')"
            >
              <img :src="moveUpIcon" alt="" />
            </button>
            <button
              class="icon-btn"
              :class="{ disabled: index === orderedTasks.length - 1 }"
              @click.stop="moveTask(index, 1)"
              :aria-label="t('taskPanel.moveDown')"
            >
              <img :src="moveDownIcon" alt="" />
            </button> -->
            <!-- <button
              class="icon-btn"
              @click.stop="handleDelete(task)"
              :aria-label="t('taskPanel.delete')"
            >
              <img :src="deleteIcon" alt="" />
            </button> -->
            <!-- <button
              class="icon-btn"
              @click.stop="handleView(task)"
              :aria-label="t('taskPanel.view')"
            >
              <img :src="viewLogIcon" alt="" />
            </button> -->
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineExpose } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import taskIcon from "@/assets/imgs/engineering-manage/task-item-icon.png";
import moveUpIcon from "@/assets/imgs/engineering-manage/button-top-active.png";
import moveDownIcon from "@/assets/imgs/engineering-manage/button-bottom-active.png";
import viewLogIcon from "@/assets/imgs/engineering-manage/button-rizhi.png";
import deleteIcon from "@/assets/imgs/engineering-manage/button-del.png";

const router = useRouter();
const { t } = useI18n();

const props = defineProps({
  position: {
    type: Object,
    default: () => ({ top: 64, right: 204 }),
  },
  tasks: {
    type: Array,
    default: () => [],
  },
});

const panelStyle = computed(() => ({
  top: `${props.position.top}px`,
  right: `${props.position.right}px`,
}));

const normalizeProgress = (value) => {
  const numeric = Number(value);

  if (!Number.isFinite(numeric)) {
    return 0;
  }

  return Math.min(100, Math.max(0, Math.round(numeric)));
};

const TASK_STATUS_META = {
  failed: {
    labelKey: "statusFailed",
    tone: "error",
  },
  running: {
    labelKey: "statusRunning",
    tone: "running",
  },
  success: {
    labelKey: "statusSuccess",
    tone: "success",
  },
  init: {
    labelKey: "statusInit",
    tone: "waiting",
  },
  uploading: {
    labelKey: "statusUploading",
    tone: "running",
  },
  upload_success: {
    labelKey: "statusUploadSuccess",
    tone: "success",
  },
  wait_process: {
    labelKey: "statusWaitProcess",
    tone: "waiting",
  },
  upload_fail: {
    labelKey: "statusUploadFail",
    tone: "error",
  },
};

const normalizeStatus = (status) => {
  const normalized = `${status || ""}`.trim().toLowerCase();
  return TASK_STATUS_META[normalized] ? normalized : "init";
};

const normalizeTask = (task) => {
  const progress = normalizeProgress(task?.process);
  const status = normalizeStatus(task?.status);
  const statusMeta = TASK_STATUS_META[status];

  return {
    id: task?.id || "",
    projectId: task?.projectId || "",
    projectName: task?.projectName || "",
    collectionTaskName: task?.collectionTaskName || task?.taskName || "--",
    taskName: task?.taskName || "",
    status,
    statusTone: statusMeta.tone,
    statusLabel: t(`taskPanel.${statusMeta.labelKey}`),
    progress,
  };
};

const orderedTasks = computed(() =>
  Array.isArray(props.tasks) ? props.tasks.map(normalizeTask) : [],
);

const moveTask = (index, direction) => {
  const nextIndex = index + direction;

  if (nextIndex < 0 || nextIndex >= orderedTasks.value.length) {
    return;
  }

  const updated = [...orderedTasks.value];
  const temp = updated[index];
  updated[index] = updated[nextIndex];
  updated[nextIndex] = temp;
};

const handleDelete = (task) => {
  console.log("[TaskPanel] delete", task);
};

const handleView = (task) => {
  console.log("[TaskPanel] view", task);
};

const handleSelectTask = (task) => {
  if (!task?.projectId) {
    return;
  }

  if (task?.projectName && typeof window !== "undefined") {
    const STORAGE_KEY_PREFIX = "engineering-manage-project-name";
    sessionStorage.setItem(
      `${STORAGE_KEY_PREFIX}:${task.projectId}`,
      task.projectName,
    );
  }

  router.push({
    name: "EngineeringManage",
    query: {
      projectId: task.projectId,
    },
  });
};

defineExpose({
  tasks: orderedTasks,
});
</script>

<style scoped lang="less">
.task-panel {
  position: fixed;
  width: 540px;
  background: rgba(49, 51, 53, 1);
  background-size: 558px 368px;
  z-index: 1000;
  border-radius: 8px;
}

.task-panel-inner {
  padding: 20px;
  box-shadow: 0px -2px 10px 0px rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(40px);
}

.task-card {
  background-color: rgba(0, 0, 0, 0.25);
  border-radius: 4px;
  height: 90px;
  padding: 10px 10px 0 10px;
  box-sizing: border-box;
  cursor: pointer;
  transition: background 0.2s ease;

  & + .task-card {
    margin-top: 16px;
  }

  &:hover {
    background-color: rgba(255, 255, 255, 0.08);
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    box-shadow: inset 0 0 0 1px rgba(4, 169, 77, 0.72);
  }
}

.task-card-header {
  display: flex;
  align-items: center;
}

.task-icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: rgba(3, 239, 107, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
}

.task-icon {
  width: 18px;
  height: 18px;
}

.task-name {
  margin-left: 15px;
  color: rgba(255, 255, 255, 1);
  font-size: 16px;
  font-family: Arial-BoldMT, Arial, sans-serif;
  line-height: 18px;
  max-width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.task-date {
  margin-left: auto;
  max-width: 170px;
  color: rgba(255, 255, 255, 0.4);
  font-size: 14px;
  font-family: ArialMT, Arial, sans-serif;
  line-height: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.task-card-body {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 6px;
  padding-left: 51px;
}

.task-progress {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.task-status {
  font-size: 14px;
  font-family: "SourceHanSansCN-Medium", Arial, sans-serif;
  line-height: 16px;
  margin-top: 0;

  &.running {
    color: rgba(32, 203, 212, 1);
  }

  &.waiting {
    color: rgba(250, 172, 20, 1);
  }

  &.error {
    color: rgba(250, 81, 81, 1);
  }

  &.success {
    color: rgba(3, 239, 107, 1);
  }
}

.task-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.icon-btn {
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.icon-btn img {
  width: 24px;
  height: 24px;
  display: block;
}

.icon-btn.disabled {
  cursor: not-allowed;
  opacity: 0.4;
}
</style>
